<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="returns"
                            variant="page"
                            :title="$t('itemsToStockLabel')"
                            :description="$t('billing.features.returns.tooltip')"
                            icon="ti-package">
          <Toolbar class="p-mb-4">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('itemsToStockLabel') }}</div>
            </template>
            <template #end>
              <div class="p-d-flex p-flex-wrap">
                <div class="p-d-flex p-ai-center p-mr-2" v-if="$store.getters.isAccountantOrHigher">
                  <Checkbox @change="changeRequestFilters"
                            :disabled="dataTableIsLoading"
                            id="to-stock-items-show-only-not-fully-invoiced-checkbox"
                            :binary="true"
                            v-model="showOnlyPendingItems"/>
                  <div>
                    <label for="to-stock-items-show-only-not-fully-invoiced-checkbox" >
                      <div class="pointer p-p-1">{{ $t('onlyPendingItemsLabel') }}</div>
                    </label>
                  </div>
                </div>
                <CustomDropdown id="supplier"
                                class="p-mr-2"
                                :disabled="dataTableIsLoading"
                                style="min-width: 180px; max-width: 200px;"
                                ref="selectedSupplierDropdown"
                                :searchData="supplierSearchData"
                                v-model="selectedSupplier"
                                data-key="id"
                                :options="suppliersData"
                                :filterPlaceholder="$t('lineItemSearchSupplierPlaceholder')"
                                optionLabel="company_name"
                                :filter="true"
                                :filterFields="['name', 'company_name', 'reg_number']"
                                :loading="suppliersDataIsLoading"
                                @change="changeSupplier"
                                @filter="searchSuppliers"
                                :showClear="true">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <div>{{ slotProps.value.company_name }} <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></div>
                    </div>
                    <span v-else>{{ $t('lineItemSelectSupplierPlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div v-if="slotProps.option">
                      <div>{{ slotProps.option.company_name }} <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></div>
                    </div>
                  </template>
                </CustomDropdown>
                <SearchInput :disabled="dataTableIsLoading"
                             @change-processed-search-value="changeProcessedSearchValue"
                             v-model="searchData"
                             @show-spinner="isLoading = true"/>
              </div>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :dataTableIsLoading="dataTableIsLoading"
                            :showHeader="false"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                              {name: 'code', title: $t('Code'), sortable: false},
                              {name: 'name', title: $t('Name'), sortable: false},
                              {name: 'supplier', title: $t('Supplier'), sortable: false},
                              {name: 'qty', title: $t('Qty'), minWidth: '70px', sortable: false},
                              {name: 'purchase_price', title: $t('purchasePriceShortLabel'), minWidth: '70px', sortable: false},
                              {name: 'sell_price', title: $t('sellingPriceShortLabel'), minWidth: '70px', sortable: false},
                              {name: 'order', width: '40px', sortable: false},
                              {name: 'reason', title: $t('Reason'), sortable: false},
                              {name: 'comment', sortable: false},
                              // {name: 'fixed_qty', title: $t('fixedQtyLabel'), sortable: false},
                              // {name: 'fixed_qty', title: null, sortable: false},
                              {name: 'created_at', title: $t('Created'), sortable: false},
                            ]">
            <template v-slot:body>
              <tr v-for="(item, index) of items"
                  :key="index"
                  :class="{'p-highlight': isItemSelected(item.id), 'updated-item': isItemUpdated(item.id)}">
                <td>
                  <span v-if="item.position_data?.code">{{ item.position_data.code }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.position_data?.name">{{ item.position_data.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.supplier">{{ item.supplier.company_name }}</span>
                </td>
                <td>{{ formatDecimal(item.qty) }}</td>
                <td>
                  <span v-if="item.position_data?.purchase_price">{{ formatCurrency(item.position_data.purchase_price, 2, 4) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.position_data?.sell_price">{{ formatCurrency(item.position_data.sell_price, 2, 4) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <!--                  <span v-if="item.order?.uid" class="p-d-flex p-ai-center">-->
                  <!--                    <router-link :to="{ path: `/orders/${item.order.uid}` }" target="_blank" draggable="false">-->
                  <!--                      <Button class="p-button-text p-p-1" v-tooltip.top="item.order?.number ? item.order.number : null">-->
                  <!--                        <i class="ti-hummer"/>-->
                  <!--                      </Button>-->
                  <!--                    </router-link>-->
                  <!--                  </span>-->
                  <!--                  <i v-else class="ti-minus"/>-->
                  <OpenOrderButton :position="item"/>
                </td>

                <td>
                  <!--                <span v-if="item.reason">{{ ReducedPositionReasons.find(reason => reason.id === item.reason)?.label[$i18n.locale] }}</span>-->
                  <span v-if="item.reason">{{ reducedReasonLabel(item.reason) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.comment" v-tooltip.top="item.comment"><i class="ti-comment-alt" ></i></span>
                </td>

                <!--              <td>-->
                <!--                <span v-if="item.fixed_qty && +item.fixed_qty"> {{ (+item.fixed_qty).toFixed() }}/{{ (+item.qty).toFixed() }}</span>-->
                <!--                <i v-else class="ti-minus"></i>-->
                <!--              </td>-->

                <!--              <td>-->
                <!--                <PositionFixedQtyButton v-if="item.fixed_qty && +item.fixed_qty" :position="item" :isReducedPosition="true"/>-->
                <!--                <i v-else class="ti-minus"/>-->
                <!--              </td>-->

                <td class="table-date-line">
                  {{ formatBy(item.created_at, item.creator) }}
                </td>
                <td>
                  <div class="actions">
                    <!--                    style="margin: 0 auto; padding: 0px 6px"-->
                    <span class="p-d-inline-flex"
                          v-tooltip.left="{
                            ...billingActionTooltipOptions(false, $t('addItemToStockTooltip')),
                            disabled: billingDataEditingLocked || Number(item.fixed_qty || 0) >= Number(item.qty || 0)
                          }">
                      <Button :disabled="billingDataEditingLocked || Number(item.fixed_qty || 0) >= Number(item.qty || 0)"
                              style="padding: 0px 6px; height: 33px;"
                              class="p-button-secondary p-button-raised"
                              @click="addItemToWarehouse(item)">
                        <i class="ti-plus p-mr-1"/>
                        <i class="ti-package"/>
                      </Button>
                    </span>
                    <EditButton :disabled="Number(item.fixed_qty || 0) > 0" @click="editItem(item)"/>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="!itemsArrayIsEmpty"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <ReturnedPositionModal :visible="itemModal"
                                 :item="item"
                                 :taxes="taxes"
                                 @update-items="updateItems"
                                 @close="closeItemModal"/>

          <AddReducedPositionToWarehouseModal :visible="addReducedPositionToWarehouseModal"
                                              :item="item"
                                              @update-items="updateItems"
                                              @close="closeAddItemToWarehouseModal"/>
        </BillingFeatureGate>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import formatMixins from "@/mixins/formatMixins";
import ReducedPositionReasons from "@/translations/ReducedPositionReasons";
import ReturnedPositionModal from "@/pages/warehouse/components/ReturnedPositionModal.vue";
import AddReducedPositionToWarehouseModal from "@/pages/warehouse/components/AddReducedPositionToWarehouseModal";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'
import OpenOrderButton from "@/components/OpenOrderButton.vue";

export default {
  mixins: [ httpMixins, datatableMixins, formatMixins, showErrorsMixins, billingFeaturesMixins, billingRestrictionsMixin ],
  components: {OpenOrderButton, ReturnedPositionModal, AddReducedPositionToWarehouseModal, BillingFeatureGate },
  data() {
    return {
      showOnlyPendingItems: true,
      itemModal: false,
      addReducedPositionToWarehouseModal: false,
      suppliers: null,
      fieldsExpand: 'creator,order,position_data,supplier,warehouse_item',
      apiLink: 'reduced-position',
      routePath: '/returns-to-stock',
      sort: {
        column: 'id',
        direction: '-',
      },
      ReducedPositionReasons,
    }
  },
  watch: {
    suppliers(value) {
      if (value?.length) {
        this.suppliersData = [...value]
      } else {
        this.suppliersData = null
      }
    },
    '$store.state.firstLayerIsOpened'(value) {
      if (!value) {
        if (this.addReducedPositionToWarehouseModal) {
          this.closeAddItemToWarehouseModal()
        }
      }
    },
    canUseReturns(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetReturnsState()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    // this.checkRouteQuery()
    // this.getPageData()
    if (this.canUseReturns) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    getPageData() {
      if (!this.canUseReturns) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems().then(() => this.getWarehouseRequestsCount())
      // this.getSuppliers().then(() => this.checkSupplierInArray())
      this.getSuppliers().then(() => {
        if (this.canUseReturns) {
          this.checkSupplierInArray()
        }
      })
      // this.getModalData().then(() => this.checkSupplierInArray())
    },
    async getWarehouseRequestsCount() {
      if (!this.canUseReturns) return
      if (!this.userCanGetData) return
      try {
        const { status, data } = await httpClient('reduced-position/warehouse-requests-count')
        if (status === 200 && data) {
          this.$store.commit('updateWarehouseRequestsCount', data.count ?? 0)
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      }
    },
    reducedReasonLabel(id) {
      const reason = this.ReducedPositionReasons.find(reason => Number(reason.id) === Number(id))

      return reason?.label?.[this.$i18n.locale] || reason?.label?.en || ''
    },
    editItem(item) {
      if (this.billingActionIsRestricted()) return false

      this.item = {...item}
      this.itemModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeItemModal() {
      this.item = {}
      this.itemModal = false
      this.$store.commit('toggleFirstLayer', false)
      // this.item = {}
      // this.submitted = false
    },
    addItemToWarehouse(item) {
      if (this.billingActionIsRestricted()) return false

      this.item = {...item}
      this.addReducedPositionToWarehouseModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeAddItemToWarehouseModal() {
      this.item = {}
      this.addReducedPositionToWarehouseModal = false
      this.$store.commit('toggleFirstLayer', false)
      // this.item = {}
      // this.submitted = false
    },
    resetReturnsState() {
      this._abortors?.warehouseRequests?.abort?.()

      this.items = []
      this.suppliers = null
      this.item = {}

      this.itemModal = false
      this.addReducedPositionToWarehouseModal = false

      this.isLoading = false
      this.dataTableIsLoading = false

      this.$store.commit('updateWarehouseRequestsCount', 0)
      this.$store.commit('toggleFirstLayer', false)
    },
    async getItems(showSpinner = false) {
      if (!this.canUseReturns) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      if (showSpinner) this.isLoading = true
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('warehouseRequests')
      // this.isLoading = true
      try {
        // const { status, data, headers } = await httpClient(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        // const { status, data, headers } = await httpClient(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`)
        const { status, data, headers } = await httpClient(`${this.apiLink}`, {
          params: {
            supplierId: this.selectedSupplierId,
            page: this.pagination.currentPage,
            // typeId: this.selectedTypeId,
            typeId: 2,
            show_only_unfixed: +this.showOnlyPendingItems,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand
          },
          signal: ctrl?.signal,
        })

        if (!this.canUseReturns) return

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.warehouseRequests === ctrl) {
          this._abortors.warehouseRequests = null
        }
      }
    },
  },
  computed: {
    actionButtonsCount() {
      return 2
    },
  }
}
</script>